/**
 * The valance between two uppers (claude-docs/2026-10-30-venus-full-line-design.md,
 * group 3c) — the one rule the 3D view, the elevation, the estimate and the
 * crown read, so what is drawn is what is billed.
 *
 * A run's choice (`run.valance = { style: 'arched' | 'raised-panel' }`), hung
 * in every gap its uppers leave in a row over a window or doorway — between
 * two uppers of one row (one facing, one back and front line, one top), when
 * the gap is empty at the valance's height (a hood's gap is the hood's) and
 * wider than the smallest box the fill builds (`MIN_MODULE_IN`; narrower it is
 * a scribe, not a window). ONLY over an opening (the user, 2026-11-01): a spot
 * the user left with no cabinet is bare wall, and a valance anywhere else —
 * over the sink — is a valance bay (`valanceBays.js`), which hangs its own in
 * its gap, opening or not, and a click selects. Its TOP is on the uppers' line and its face flush
 * with their fronts (the user, 2026-10-31: "valence at top"), so the crown
 * runs straight across it: each valance is also a crown CARRIER
 * (`valanceCarriers`), the uppers' depth deep, so its back meets the wall and
 * its ends the uppers — the crown finds no edge to return along.
 *
 * The size is the smallest of the line's valances of that style that covers
 * the gap, trimmed down — no shorter than the catalog's "trimable to"
 * (`trimToM`); none fits, none is hung, with the reason. Its colour is the
 * run's (the two uppers'), judged as the crown is (`resolveTrimProfile`): a
 * colour the line does not price it in leaves it out, never swapped. And it
 * must clear the window under it (the user, 2026-10-31) — or a doorway or
 * pass-through: where one's top reaches above the valance's bottom edge
 * anywhere across its width — the arch's rise counts, so an arch can frame a
 * window that reaches into it — it is left out, with the reason. The
 * opening's own top, not its casing; read from the room's openings with their
 * geometry (`roomWindows.js` fills the slim copies in).
 *
 * Pure; metres.
 */
const { MIN_MODULE_IN } = require('../cabinet-runs/moduleSolver');
const { colourCodeFor } = require('../cabinet-runs/vendorSizes');
const { findRunByIdOrAlias } = require('../cabinet-runs/runIds');
const { familyOf } = require('../cabinet-runs/families');
const {
  isValanceBay,
  hangsBeside,
  valanceSizeFor,
  valanceBoardOf,
  valanceStyleFor,
  AT_THE_END,
} = require('../cabinet-runs/valanceBays');
const { carrierOutlines } = require('./fronts');
const { resolveTrimProfile, inchesText, VALANCE_NAMES } = require('./profiles');

const IN = 0.0254;
/** Lines and tops within this are one (the trim's touch tolerance). */
const SAME_M = 0.01;
const MIN_GAP_M = MIN_MODULE_IN * IN;
// a window this far off the uppers' back line is in their wall (its
// thickness and a scribe), not across the room
const WINDOW_WALL_M = 0.3;

/**
 * The arch along a valance's bottom edge: how far it rises at the middle and
 * the straight foot at each end (the catalog's drawings, pp. 25–26) — the 5½"
 * arched board keeps half its height at the middle; the 12" raised-panel one
 * stands its full height at the ends and keeps 5" at the middle.
 */
const ARCH = Object.freeze({
  arched: { rise: (h) => h / 2, footM: 2 * IN },
  'raised-panel': { rise: (h) => h - 5 * IN, footM: 3 * IN },
});
/** The raised-panel valance's frame round its two panels (and between them). */
const PANEL_INSET_M = 1.5 * IN;
// samples along an arch: enough that it reads as a curve at kitchen scale
const ARCH_SAMPLES = 24;

const hasGeometry = (o) =>
  o?.center &&
  o?.dimensions &&
  [o.center.x, o.center.y, o.center.z].every(Number.isFinite) &&
  o.dimensions.x > 0 &&
  o.dimensions.y > 0 &&
  o.dimensions.z > 0;

/** A box's frame: its facing (local +Z), its along (local +X). */
function frameOf(o) {
  const ry = o.rotation?.y || 0;
  return {
    ry,
    facing: { x: Math.sin(ry), z: Math.cos(ry) },
    along: { x: Math.cos(ry), z: -Math.sin(ry) },
  };
}
const dot = (a, b) => a.x * b.x + a.z * b.z;

/** Where an upper sits in its row: its span along, its back and front lines. */
function placeOf(o, frame) {
  const u = dot(o.center, frame.along);
  const v = dot(o.center, frame.facing);
  return {
    o,
    lo: u - o.dimensions.x / 2,
    hi: u + o.dimensions.x / 2,
    back: v - o.dimensions.z / 2,
    front: v + o.dimensions.z / 2,
    top: o.center.y + o.dimensions.y / 2,
  };
}

/** A run's uppers, in rows: one facing, one back and front line, one top. */
function rowsOf(uppers) {
  const rows = [];
  for (const o of uppers) {
    const frame = frameOf(o);
    const p = placeOf(o, frame);
    const row = rows.find(
      (r) =>
        Math.abs(Math.sin(r.frame.ry - frame.ry)) < 1e-3 &&
        Math.cos(r.frame.ry - frame.ry) > 0 &&
        Math.abs(r.places[0].back - p.back) < SAME_M &&
        Math.abs(r.places[0].front - p.front) < SAME_M &&
        Math.abs(r.places[0].top - p.top) < SAME_M
    );
    if (row) row.places.push(placeOf(o, row.frame));
    else rows.push({ frame, places: [p] });
  }
  for (const r of rows) r.places.sort((a, b) => a.lo - b.lo);
  return rows;
}

/** Does anything stand in the gap at the valance's height (a hood, a chimney)? */
function gapIsOccupied(objects, frame, gap, band, skip) {
  return objects.some((o) => {
    if (skip.has(o) || !hasGeometry(o)) return false;
    const top = o.center.y + o.dimensions.y / 2;
    const bottom = o.center.y - o.dimensions.y / 2;
    if (top <= band.bottom + SAME_M || bottom >= band.top - SAME_M)
      return false;
    // its plan footprint, not its centre: a 30"-deep fridge in the gap
    // stands well out past the uppers' front line
    const pts = carrierOutlines(o).flat();
    if (pts.length === 0) return false;
    const us = pts.map((p) => dot(p, frame.along));
    const vs = pts.map((p) => dot(p, frame.facing));
    const overlaps = (lo, hi, a, b) => Math.min(hi, b) - Math.max(lo, a);
    return (
      overlaps(Math.min(...us), Math.max(...us), gap.lo, gap.hi) > SAME_M &&
      overlaps(Math.min(...vs), Math.max(...vs), gap.back, gap.front) > SAME_M
    );
  });
}

const styleWord = (style) => VALANCE_NAMES[style].toLowerCase();
const article = (word) => `${/^[aeiou]/.test(word) ? 'an' : 'a'} ${word}`;
const capitalized = (text) => text[0].toUpperCase() + text.slice(1);

/**
 * One gap's valance, or the reason it has none — in `style`; `bay` is the
 * valance bay standing in the gap (`valanceBays.js`), which a click on it
 * selects, or null for a gap the run's Valance row hangs one in.
 */
function valanceFor(room, run, family, row, a, b, index, style, bay) {
  const gap = {
    lo: a.hi,
    hi: b.lo,
    back: a.back,
    front: a.front,
  };
  const lengthM = gap.hi - gap.lo;
  const note = (reason) => ({
    note: { runId: run.id, carrierId: bay ? bay.id : a.o.id, reason },
  });
  const size = valanceSizeFor(family, style, lengthM);
  if (!size)
    return note(
      `${family.name} makes no ${styleWord(style)} that fits a ${inchesText(
        Math.round(lengthM / IN)
      )} gap, so this one has none`
    );
  const codes = [colourCodeFor(family, a.o), colourCodeFor(family, b.o)];
  const judged = resolveTrimProfile(
    family,
    'valance',
    size.id,
    size.colours ? codes : []
  );
  if (!judged.profile) return note(judged.reason);
  const { frame } = row;
  const heightM = size.heightM;
  const boardM = size.depthM;
  const midU = (gap.lo + gap.hi) / 2;
  const boardV = gap.front - boardM / 2;
  const arch = ARCH[style];
  return {
    valance: {
      id: `valance-${run.id}-${index}`,
      runId: run.id,
      style,
      sku: size.sku,
      profileId: size.id,
      label: size.label,
      colourCode: size.colours ? codes[0] : null,
      lengthM,
      heightM,
      boardM,
      archRiseM: arch.rise(heightM),
      archFootM: Math.min(arch.footM, lengthM / 4),
      ...(style === 'raised-panel' ? { panelInsetM: PANEL_INSET_M } : {}),
      // the board's own box: its face on the uppers' front line
      center: {
        x: frame.along.x * midU + frame.facing.x * boardV,
        y: a.top - heightM / 2,
        z: frame.along.z * midU + frame.facing.z * boardV,
      },
      rotation: { x: 0, y: frame.ry, z: 0 },
      facing: frame.facing,
      // the uppers it hangs between — what its crown is carried by
      betweenIds: [a.o.id, b.o.id],
      // the valance bay it hangs in, which a click on it selects
      ...(bay ? { bayId: bay.id } : {}),
      gap,
    },
  };
}

/** The valance bay standing in this gap of `row`, if any. */
function bayInGap(bays, row, gap) {
  return (
    bays.find((o) => {
      const frame = frameOf(o);
      if (
        Math.abs(Math.sin(row.frame.ry - frame.ry)) >= 1e-3 ||
        Math.cos(row.frame.ry - frame.ry) <= 0
      )
        return false;
      const u = dot(o.center, row.frame.along);
      return u > gap.lo + SAME_M && u < gap.hi - SAME_M;
    }) || null
  );
}

/**
 * Every valance in the room, and the reasons a gap that asked for one has
 * none.
 *
 * @param {Object} room  `objects`, `cabinetRuns`
 * @returns {{ valances: Valance[], notes: { runId, carrierId, reason }[] }}
 */
function cabinetValances(room) {
  const all = (room?.objects || []).filter(hasGeometry);
  // a valance bay is the valance's own handle, never what fills its gap
  const objects = all.filter((o) => !isValanceBay(o));
  const openings = openingsOf(room);
  const valances = [];
  const notes = [];
  for (const run of room?.cabinetRuns || []) {
    if (!run) continue;
    const ofRun = (o) => findRunByIdOrAlias([run], o.runId);
    // the run's Valance row hangs one in every gap over an opening; a valance
    // bay in its own
    const rowStyle = VALANCE_NAMES[run.valance?.style]
      ? run.valance.style
      : null;
    const bays = all.filter((o) => isValanceBay(o) && ofRun(o));
    if (!rowStyle && !bays.length) continue;
    const family = familyOf(run);
    const uppers = objects.filter((o) => hangsBeside(o) && ofRun(o));
    const hungBays = new Set();
    for (const row of rowsOf(uppers)) {
      for (let i = 0; i + 1 < row.places.length; i++) {
        const a = row.places[i];
        const b = row.places[i + 1];
        const lengthM = b.lo - a.hi;
        if (lengthM < MIN_GAP_M) continue;
        const gap = { lo: a.hi, hi: b.lo, back: a.back, front: a.front };
        const bay = bayInGap(bays, row, gap);
        const style = bay ? valanceStyleFor(run, bay) : rowStyle;
        if (!style) continue;
        // the run's row: only where its uppers stop for a window or doorway
        if (!bay && !openings.some((w) => spanBehind(w, row.frame.facing, gap)))
          continue;
        if (bay) hungBays.add(bay);
        const band = {
          top: a.top,
          bottom: a.top - (valanceBoardOf(family, style)?.heightM ?? 0),
        };
        if (gapIsOccupied(objects, row.frame, gap, band, new Set([a.o, b.o])))
          continue;
        const out = valanceFor(
          room,
          run,
          family,
          row,
          a,
          b,
          valances.length,
          style,
          bay
        );
        const covered = out.valance && coveredOpening(out.valance, openings);
        if (covered)
          notes.push({
            runId: run.id,
            carrierId: bay ? bay.id : a.o.id,
            reason: `${capitalized(article(styleWord(style)))} here would hang over the top of the ${covered.word}, so this gap has none`,
          });
        else if (out.valance) valances.push(out.valance);
        else notes.push(out.note);
      }
    }
    for (const bay of bays)
      if (!hungBays.has(bay))
        notes.push({
          runId: run.id,
          carrierId: bay.id,
          reason: AT_THE_END,
        });
  }
  return { valances, notes };
}

/**
 * Each valance as a crown carrier: a box the uppers' depth deep between them,
 * at the valance's height, wearing their crown — so the crown's pieces rule
 * sees no edge at the gap. `trimCarrierIds` are the uppers', which the pieces
 * report in its place (the valance is no object of the room).
 */
function valanceCarriers(room, valances) {
  const byId = new Map((room?.objects || []).map((o) => [o.id, o]));
  return valances.flatMap((v) => {
    const a = byId.get(v.betweenIds[0]);
    if (!a?.cabinetCrown) return [];
    const depth = v.gap.front - v.gap.back;
    const midV = (v.gap.back + v.gap.front) / 2;
    const midU = (v.gap.lo + v.gap.hi) / 2;
    return [
      {
        id: v.id,
        type: 'cabinet-valance',
        runId: v.runId,
        center: {
          x: v.facing.z * midU + v.facing.x * midV,
          y: v.center.y,
          z: -v.facing.x * midU + v.facing.z * midV,
        },
        dimensions: { x: v.lengthM, y: v.heightM, z: depth },
        rotation: v.rotation,
        cabinetCrown: a.cabinetCrown,
        trimCarrierIds: v.betweenIds,
      },
    ];
  });
}

/**
 * The bottom edge's height at `x` along a valance, in its own frame (origin
 * at its centre): the arch — half an ellipse `rx` wide, `archRiseM` high —
 * between the straight feet at its ends.
 */
function archBottomAt(v, x) {
  const rx = v.lengthM / 2 - v.archFootM;
  const bottom = -v.heightM / 2;
  if (!(rx > 0) || Math.abs(x) >= rx) return bottom;
  return bottom + v.archRiseM * Math.sqrt(1 - (x / rx) ** 2);
}

/** `[x0 … x1]` in `n` equal steps, both ends included. */
const steps = (x0, x1, n) =>
  Array.from({ length: n + 1 }, (_, i) => x0 + ((x1 - x0) * i) / n);

/**
 * A valance's face outline in its own frame — `[[x, y], …]`, counter-
 * clockwise from its bottom-left corner: the arch along its bottom, straight
 * across its top. The 3D view extrudes it, the elevation strokes it.
 */
function valanceOutline(v) {
  const half = v.lengthM / 2;
  const top = v.heightM / 2;
  const bottom = steps(-half, half, ARCH_SAMPLES * 2).map((x) => [
    x,
    archBottomAt(v, x),
  ]);
  return [...bottom, [half, top], [-half, top]];
}

/**
 * A raised-panel valance's two panels, each an outline like
 * `valanceOutline`'s: inside its frame (`panelInsetM`) and either side of
 * the frame's middle stile, the bottom following the arch. `[]` for an
 * arched board.
 */
function valancePanelOutlines(v) {
  const r = v.panelInsetM;
  if (!(r > 0)) return [];
  const top = v.heightM / 2 - r;
  const half = v.lengthM / 2;
  return [
    [-half + r, -r / 2],
    [r / 2, half - r],
  ].map(([x0, x1]) => {
    const bottom = steps(x0, x1, ARCH_SAMPLES).map((x) => [
      x,
      Math.min(archBottomAt(v, x) + r, top),
    ]);
    return [...bottom, [x1, top], [x0, top]];
  });
}

/**
 * Where an opening meets a gap of a row facing `facing`, along it — `[x0, x1]`
 * from the gap's middle — or null when it is not in the wall behind the gap:
 * across the room, or off to one side.
 */
function spanBehind(w, facing, gap) {
  // in the wall behind the uppers, not across the room
  if (Math.abs(dot(w.center, facing) - gap.back) > WINDOW_WALL_M) return null;
  const along = { x: facing.z, z: -facing.x };
  const wf = frameOf(w);
  const half =
    Math.abs((w.dimensions.x / 2) * dot(wf.along, along)) +
    Math.abs(((w.dimensions.z || 0) / 2) * dot(wf.facing, along));
  const u = dot(w.center, along);
  const midU = (gap.lo + gap.hi) / 2;
  const x0 = Math.max(u - half, gap.lo) - midU;
  const x1 = Math.min(u + half, gap.hi) - midU;
  return x1 - x0 > SAME_M ? [x0, x1] : null;
}

/**
 * The opening in the wall behind its gap whose top this valance would hang
 * over — a window, a doorway, a pass-through — or null. The lowest the
 * valance comes over one is at its outer edge (the arch rises toward the
 * middle).
 */
function coveredOpening(v, openings) {
  return openings.find((w) => {
    const span = spanBehind(w, v.facing, v.gap);
    if (!span) return false;
    const [x0, x1] = span;
    const lowest = Math.min(archBottomAt(v, x0), archBottomAt(v, x1));
    const top = w.center.y + w.dimensions.y / 2;
    return top > v.center.y + lowest + 1e-6;
  });
}

/** What each kind of opening is called in a valance's reason. */
const OPENING_WORDS = Object.freeze({
  windows: 'window',
  doors: 'doorway',
  openings: 'opening',
});

/**
 * A room's windows, doors and openings that carry their geometry (a slim copy
 * cannot be read), each with what it is called.
 */
const openingsOf = (room) =>
  Object.entries(OPENING_WORDS).flatMap(([key, word]) =>
    (room?.[key] || [])
      .filter((w) => w?.center && w?.dimensions?.x > 0 && w.dimensions.y > 0)
      .map((w) => ({ ...w, word }))
  );

/** Is this a valance's crown carrier (`valanceCarriers`)? */
const isValanceCarrier = (o) => o?.type === 'cabinet-valance';

module.exports = {
  cabinetValances,
  valanceCarriers,
  isValanceCarrier,
  valanceOutline,
  valancePanelOutlines,
};
